import { Button } from '@cimpress/react-components';
import { useCallback, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useUpdateEffect } from '../../../tools';
import { Control } from '../../layout';
import type { BrandColor } from './types';
import { BrandColorAssetEditor } from './ColorPaletteAssetEditor';

export interface BrandColorListEditorProps {
    initialValue?: BrandColor[];
    onChange: (value?: BrandColor[]) => void;
}

interface BrandColorListItem {
    id: string;
    asset?: BrandColor;
}

export const BrandColorListEditor = ({ initialValue, onChange }: BrandColorListEditorProps) => {
    const [BrandColorList, setBrandColorList] = useState<BrandColorListItem[]>(() =>
        (initialValue ?? []).map((asset) => ({ id: uuidv4(), asset })),
    );

    const addColorAsset = () => {
        setBrandColorList((prev) => [...prev, { id: uuidv4() }]);
    };

    const removeColorAsset = useCallback((id: string) => {
        setBrandColorList((prev) => prev.filter((item) => item.id !== id));
    }, []);

    const updateColorAsset = useCallback((id: string, asset: BrandColor) => {
        setBrandColorList((prev) => {
            const existingAsset = prev.find((a) => a.id === id)!;

            // This is technically "illegal" in React as we should avoid mutating the state,
            // but this whole component doesn't really care about the wrapper object reference.
            // The state update will be picked up by React anyway because we're returning a new array below.
            existingAsset.asset = asset;

            return [...prev];
        });
    }, []);

    useUpdateEffect(() => {
        if (BrandColorList.every(({ asset }) => asset?.color)) {
            onChange(BrandColorList.map((item) => item.asset!));
        } else {
            onChange(undefined);
        }
    }, [BrandColorList, onChange]);

    return (
        <Control>
            {BrandColorList.map(({ id, asset }) => (
                <BrandColorAssetEditor
                    key={id}
                    assetId={id}
                    initialValue={asset}
                    onChange={updateColorAsset}
                    onRemove={removeColorAsset}
                />
            ))}

            <Button variant="primary" blockLevel onClick={addColorAsset}>
                Add new color
            </Button>
        </Control>
    );
};
